// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ITEM_TYPE_DESCRIPTIONS,
  ITEM_TYPE_EXAMPLES,
  ITEM_TYPE_ICONS,
  ITEM_TYPE_LABELS,
  type ItemType,
} from '@/items/constants/item-types';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  styled,
} from '@mui/material';

interface SelectableCardProps {
  selected?: boolean;
}

const SelectableCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<SelectableCardProps>(({ theme, selected }) => ({
  cursor: 'pointer',
  border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: selected
      ? theme.palette.action.selected
      : theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 48,
  height: 48,
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  flexShrink: 0,
}));

/**
 * Configuration for a single item type option.
 */
interface ItemTypeOption {
  type: ItemType;
  label: string;
  description: string;
  examples: string;
}

/**
 * All available item type options for the picker.
 */
const ITEM_TYPE_OPTIONS: ItemTypeOption[] = [
  {
    type: 'SERVICE',
    label: ITEM_TYPE_LABELS.SERVICE,
    description: ITEM_TYPE_DESCRIPTIONS.SERVICE,
    examples: ITEM_TYPE_EXAMPLES.SERVICE,
  },
  {
    type: 'DEVICE',
    label: ITEM_TYPE_LABELS.DEVICE,
    description: ITEM_TYPE_DESCRIPTIONS.DEVICE,
    examples: ITEM_TYPE_EXAMPLES.DEVICE,
  },
  {
    type: 'BULK',
    label: ITEM_TYPE_LABELS.BULK,
    description: ITEM_TYPE_DESCRIPTIONS.BULK,
    examples: ITEM_TYPE_EXAMPLES.BULK,
  },
];

interface ItemTypePickerProps {
  /**
   * Currently selected item type, or null if none selected.
   */
  selectedType: ItemType | null;
  /**
   * Callback when an item type is selected.
   */
  onSelect: (type: ItemType) => void;
  /**
   * Item types to exclude from the picker.
   */
  excludeTypes?: ItemType[];
}

/**
 * A picker component for selecting an item type (Service, Device, or Bulk).
 * Renders selectable cards with icons, labels, descriptions, and examples.
 *
 * Used in:
 * - Triage classification modal
 * - Create item flow
 */
export function ItemTypePicker(props: ItemTypePickerProps) {
  const { selectedType, onSelect, excludeTypes = [] } = props;

  const visibleOptions = ITEM_TYPE_OPTIONS.filter(
    (option) => !excludeTypes.includes(option.type)
  );

  return (
    <Stack spacing={2}>
      {visibleOptions.map((option) => {
        const Icon = ITEM_TYPE_ICONS[option.type];
        const isSelected = selectedType === option.type;
        return (
          <SelectableCard
            key={option.type}
            selected={isSelected}
            onClick={() => onSelect(option.type)}
          >
            <CardContent
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                '&:last-child': { pb: 2 },
              }}
            >
              <IconContainer>
                <Icon color="action" fontSize="medium" />
              </IconContainer>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {option.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.description}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic', mt: 0.5 }}
                >
                  Ex: {option.examples}
                </Typography>
              </Box>
            </CardContent>
          </SelectableCard>
        );
      })}
    </Stack>
  );
}
